import { CHAT_HISTORY, PROTOCOL_VERSION, parseHostMessage, type ChatMessage, type HostMessage, type LobbyMessage, type Role, type StateMessage } from './protocol.ts';

/** Client side of any transport. */
export interface ClientLink {
  send(data: string): void;
  onMessage(cb: (data: string) => void): void;
}

export interface ClientSnapshot {
  readonly id: string | null;
  readonly token: string | null;
  readonly role: Role | null;
  readonly lobby: LobbyMessage | null;
  readonly game: StateMessage | null;
  readonly lastError: { readonly code: string; readonly message: string } | null;
  readonly lastRejection: { readonly reason: string; readonly detail?: string } | null;
  readonly chat: readonly ChatMessage[];
}

export interface ClientSession {
  readonly snapshot: ClientSnapshot;
  hello(name: string, role: Role, token?: string): void;
  start(gameId: string, options?: Record<string, unknown>): void;
  /** Owner only: change a game's settings for the room (applies to later starts). */
  configure(gameId: string, options: Record<string, unknown>): void;
  rename(name: string): void;
  kick(memberId: string): void;
  say(text: string): void;
  presence(away: boolean): void;
  /** iOS app: register this device for "Your turn" notifications (FR-PUSH-01). */
  push(token: string): void;
  /** Owner only: abandon the current game and return everyone to the lobby. */
  end(): void;
  /** Sends an action stamped with the latest known seq. */
  act(action: { type: string } & Record<string, unknown>): void;
  isOwner(): boolean;
  subscribe(cb: (s: ClientSnapshot) => void): () => void;
}

export function createClientSession(link: ClientLink): ClientSession {
  let snap: ClientSnapshot = { id: null, token: null, role: null, lobby: null, game: null, lastError: null, lastRejection: null, chat: [] };
  const listeners = new Set<(s: ClientSnapshot) => void>();
  const emit = () => {
    for (const l of listeners) l(snap);
  };

  link.onMessage((raw) => {
    const msg = parseHostMessage(raw);
    if (!msg) return;
    snap = reduce(snap, msg);
    emit();
  });

  const out = (m: unknown) => link.send(JSON.stringify(m));

  return {
    get snapshot() {
      return snap;
    },
    hello: (name, role, token) => out(token ? { t: 'hello', v: PROTOCOL_VERSION, name, role, token } : { t: 'hello', v: PROTOCOL_VERSION, name, role }),
    start: (gameId, options) => out(options ? { t: 'start', gameId, options } : { t: 'start', gameId }),
    configure: (gameId, options) => out({ t: 'configure', gameId, options }),
    rename: (name) => out({ t: 'rename', name }),
    kick: (memberId) => out({ t: 'kick', memberId }),
    say: (text) => out({ t: 'chat', text }),
    presence: (away) => out({ t: 'presence', away }),
    push: (token) => out({ t: 'push', token }),
    end: () => out({ t: 'end' }),
    act: (action) => {
      snap = { ...snap, lastRejection: null };
      out({ t: 'act', action, seq: snap.game?.seq ?? 0 });
    },
    isOwner: () => snap.id !== null && snap.lobby?.ownerId === snap.id,
    subscribe(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

function reduce(s: ClientSnapshot, msg: HostMessage): ClientSnapshot {
  switch (msg.t) {
    case 'welcome':
      return { ...s, id: msg.id, token: msg.token, role: msg.role, lastError: null };
    case 'lobby':
      return { ...s, lobby: msg, game: msg.gameId === null ? null : s.game };
    case 'state':
      return { ...s, game: msg, lastError: null };
    case 'chat':
      return { ...s, chat: msg.history ? [...msg.messages] : [...s.chat, ...msg.messages].slice(-CHAT_HISTORY) };
    case 'rejected':
      return { ...s, lastRejection: msg.detail === undefined ? { reason: msg.reason } : { reason: msg.reason, detail: msg.detail } };
    case 'error':
      return { ...s, lastError: { code: msg.code, message: msg.message } };
  }
}
